import React from 'react'
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps,
} from 'recharts'

export interface TimeSeriesPoint {
  month: string
  current: number
  previous: number
  /** Optional: date for this point (current period), e.g. "13 Jan 2026" — shown in tooltip */
  currentDate?: string
  /** Optional: same date in previous year, e.g. "13 Jan 2025" — shown in tooltip */
  previousDate?: string
  tooltipPrimaryLabel?: string
  tooltipPrimaryValue?: number
  tooltipSecondaryLabel?: string
  tooltipSecondaryValue?: number
}

function formatYAxis(value: number): string {
  if (value >= 1_000_000) return value / 1_000_000 + 'M'
  if (value <= -1_000_000) return value / 1_000_000 + 'M'
  if (Math.abs(value) >= 1_000) return value / 1_000 + 'k'
  return String(value)
}

const containerStyle: React.CSSProperties = {
  background: '#fff',
  padding: '14px 18px',
  borderRadius: '8px',
  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
  fontSize: '14px',
  fontFamily: 'Lexend Deca, sans-serif',
  minWidth: '180px',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  fontWeight: 500,
}

const valueStyle: React.CSSProperties = {
  fontWeight: 600,
}

function formatTooltipNumber(num: number): string {
  return Math.round(num).toLocaleString('en-US')
}

export interface XeroStyleTimeSeriesChartProps {
  data: TimeSeriesPoint[]
  /** Current period line and area color (e.g. #7B3FE4 for purple) */
  currentColor?: string
  /** Area fill (e.g. rgba(123, 63, 228, 0.08)) */
  fillColor?: string
  currentLabel?: string
  previousLabel?: string
  height?: number
  /** Optional Y domain [min, max] */
  domain?: [number, number]
  /** Optional Y-axis tick formatter (e.g. (v) => `${v}%` for margin charts) */
  yAxisTickFormatter?: (value: number) => string
  /** Optional suffix for tooltip values (e.g. '%'); if set, tooltip shows value + suffix instead of locale number */
  valueSuffix?: string
  /** Previous period line stroke (e.g. #9CA3AF) */
  previousStroke?: string
  /** Previous period line dash (e.g. "5 5") */
  previousStrokeDasharray?: string
  /** CartesianGrid stroke (e.g. #e5e7eb) */
  gridStroke?: string
  /** ReferenceLine at 0 stroke (e.g. #9ca3af) */
  zeroLineStroke?: string
  /** Optional: format tooltip values (e.g. (v) => v.toFixed(2) for ratios). When set, used instead of valueSuffix/number formatting. */
  tooltipValueFormatter?: (value: number) => string
}

function formatTooltipValue(
  value: number,
  valueSuffix?: string,
  customFormatter?: (value: number) => string
): string {
  if (customFormatter) return customFormatter(value)
  if (valueSuffix) return Number(value).toFixed(2) + valueSuffix
  return formatTooltipNumber(value)
}

export default function XeroStyleTimeSeriesChart({
  data,
  currentColor = '#7B3FE4',
  fillColor = 'rgba(123, 63, 228, 0.08)',
  currentLabel = 'Current',
  previousLabel = 'Previous',
  height = 320,
  domain,
  yAxisTickFormatter,
  valueSuffix,
  previousStroke = '#9CA3AF',
  previousStrokeDasharray = '5 5',
  gridStroke = '#e5e7eb',
  zeroLineStroke = '#9ca3af',
  tooltipValueFormatter,
}: XeroStyleTimeSeriesChartProps) {
  const XeroTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload || payload.length < 2) return null
    const point = payload[0]?.payload as TimeSeriesPoint | undefined
    const currentEntry = payload.find((p) => p.dataKey === 'current')
    const previousEntry = payload.find((p) => p.dataKey === 'previous')
    const current = (currentEntry?.value as number) ?? 0
    const previous = (previousEntry?.value as number) ?? 0
    const currentDateLabel = point?.currentDate ?? currentLabel
    const previousDateLabel = point?.previousDate ?? previousLabel
    const hasExtraRows = point?.tooltipPrimaryLabel && point?.tooltipSecondaryLabel
    return (
      <div style={containerStyle}>
        {hasExtraRows && (
          <>
            <div style={rowStyle}>
              <span>{point.tooltipPrimaryLabel}</span>
              <span style={valueStyle}>{formatTooltipNumber(point.tooltipPrimaryValue ?? 0)}</span>
            </div>
            <div style={{ ...rowStyle, marginTop: 6, fontWeight: 400 }}>
              <span>{point.tooltipSecondaryLabel}</span>
              <span style={{ ...valueStyle, fontWeight: 400 }}>{formatTooltipNumber(point.tooltipSecondaryValue ?? 0)}</span>
            </div>
            <div style={{ margin: '10px 0', borderTop: '1px solid #d1d5db' }} />
          </>
        )}
        <div style={rowStyle}>
          <span style={{ fontWeight: 600 }}>{currentDateLabel}</span>
          <span style={valueStyle}>{formatTooltipValue(current, valueSuffix, tooltipValueFormatter)}</span>
        </div>
        <div style={{ ...rowStyle, marginTop: 6, fontWeight: 400 }}>
          <span>{previousDateLabel}</span>
          <span style={{ ...valueStyle, fontWeight: 400 }}>{formatTooltipValue(previous, valueSuffix, tooltipValueFormatter)}</span>
        </div>
      </div>
    )
  }

  if (!data?.length) return null

  // Dynamic x-axis: show every 2nd or 3rd tick when many points to avoid crowding (e.g. Mar, May, Jul, Sept, Nov, Jan)
  const n = data.length
  const xInterval =
    n > 8 ? 2   // e.g. 12 months → show 4 ticks
    : n > 4 ? 1 // e.g. 6–8 points → show every 2nd (Mar, May, Jul…)
    : 0         // 4 or fewer → show all

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 10, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={gridStroke} />
          <XAxis
            dataKey="month"
            interval={xInterval}
            tick={{ fill: '#374151', fontSize: 11, fontFamily: 'Lexend Deca' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => (yAxisTickFormatter ? yAxisTickFormatter(Number(v)) : formatYAxis(Number(v)))}
            tick={{ fill: '#374151', fontSize: 11, fontFamily: 'Lexend Deca' }}
            axisLine={false}
            tickLine={false}
            domain={domain ?? ['auto', 'auto']}
          />
          <Tooltip content={<XeroTooltip />} cursor={{ stroke: '#333', strokeWidth: 1 }} />
          <ReferenceLine y={0} stroke={zeroLineStroke} strokeWidth={1} />
          {/* Previous period: grey dashed line (render first so it sits behind) */}
          <Line
            type="monotone"
            dataKey="previous"
            stroke={previousStroke}
            strokeDasharray={previousStrokeDasharray}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {/* Area fill for current */}
          <Area
            type="monotone"
            dataKey="current"
            fill={fillColor}
            stroke="none"
            isAnimationActive={false}
          />
          {/* Current period: solid line on top */}
          <Line
            type="monotone"
            dataKey="current"
            stroke={currentColor}
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
